// src/components/ReadjudicacionMasiva/Abogados/AbogadoAutocomplete.js

import React, { useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    TextField,
    CircularProgress,
    Avatar,
    Box,
    Typography
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import useReadjudicacion from '../../../hooks/useReadjudicacion';

const useStyles = makeStyles((theme) => ({
    autocomplete: {
        width: '100%'
    },
    option: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(1)
    },
    avatar: {
        width: 40,
        height: 40,
        backgroundColor: theme.palette.primary.main
    },
    abogadoInfo: {
        flexGrow: 1
    },
    abogadoNombre: {
        fontWeight: 600,
        fontSize: '0.875rem'
    },
    abogadoMatricula: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary
    }
}));

const AbogadoAutocomplete = ({ value, onChange, disabled, label = "Seleccionar Abogado" }) => {
    const classes = useStyles();
    const { buscarAbogados } = useReadjudicacion();

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const handleInputChange = async (event, newInputValue) => {
        setInputValue(newInputValue);

        if (newInputValue.length >= 2) {
            setLoading(true);
            try {
                const abogados = await buscarAbogados(newInputValue);
                setOptions(abogados);
            } catch (error) {
                console.error('Error al buscar abogados:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        } else {
            setOptions([]);
        }
    };

    const handleChange = (event, newValue) => {
        onChange(newValue);
    };

    const renderOption = (props, option) => (
        <li {...props} key={option.Id}>
            <Box className={classes.option}>
                <Avatar className={classes.avatar}>
                    <PersonIcon />
                </Avatar>
                <Box className={classes.abogadoInfo}>
                    <Typography className={classes.abogadoNombre}>
                        {option.NombreCompleto}
                    </Typography>
                    <Typography className={classes.abogadoMatricula}>
                        Matrícula: {option.Matricula}
                        {option.Domicilio && ` • ${option.Domicilio}`}
                    </Typography>
                </Box>
            </Box>
        </li>
    );

    return (
        <Autocomplete
            className={classes.autocomplete}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={value}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={options}
            isOptionEqualToValue={(option, value) => option.Id === value?.Id}
            getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.NombreCompleto || '';
            }}
            renderOption={renderOption}
            loading={loading}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    size="small"
                    placeholder="Buscar por nombre o matrícula"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
            noOptionsText="Ingrese al menos 2 caracteres para buscar"
        />
    );
};

export default AbogadoAutocomplete;
